import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiUser, FiEdit } from 'react-icons/fi';
import { isFluxDomain } from '../../utils/fluxTheme';
import { NotificationMarquee } from './AgentNotifications';
import './AgentDashboard.css';

// Pipeline column colours (id → colour)
const PIPELINE_COLORS = {
  'col-submitted':    { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', icon: '📋' },
  'col-needs-info':   { bg: '#fffbeb', border: '#fde68a', text: '#b45309', icon: '❓' },
  'col-pending-docs': { bg: '#fdf4ff', border: '#e9d5ff', text: '#7e22ce', icon: '📎' },
  'col-completed':    { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d', icon: '✅' },
  'col-lost':         { bg: '#fef2f2', border: '#fecaca', text: '#dc2626', icon: '❌' },
};
function getColStyle(colId) {
  return PIPELINE_COLORS[colId] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', icon: '📌' };
}

function AgentDashboard({ token }) {
  const isFlux = isFluxDomain();
  const [stats, setStats] = useState(null);
  const [pipelineCounts, setPipelineCounts] = useState(null); // { columns: [{id,title,count}], total }
  const [has2FA, setHas2FA] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // ── Enable/Disable toggle request state ──
  const [agentEnabled, setAgentEnabled] = useState(null);   // null = loading
  const [agentProfile, setAgentProfile] = useState(null);   // full profile for context
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [toggleReason, setToggleReason] = useState('');
  const [submittingToggle, setSubmittingToggle] = useState(false);
  const [hasPendingToggle, setHasPendingToggle] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchProfileAndStatus();
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      const res = await axios.get('/api/agent/pipeline-board', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const board = res.data?.board;
      if (!board || !board.columns) return;
      const total = board.columns.reduce((s, c) => s + (c.leadIds?.length || 0), 0);
      setPipelineCounts({
        total,
        columns: board.columns.map(c => ({
          id: c.id,
          title: c.title,
          count: c.leadIds?.length || 0,
          pct: total > 0 ? Math.round(((c.leadIds?.length || 0) / total) * 100) : 0
        }))
      });
    } catch (e) {
      // non-fatal — pipeline section simply won't appear
    }
  };

  const fetchProfileAndStatus = async () => {
    try {
      const [profileRes, requestsRes] = await Promise.all([
        axios.get('/api/agent/profile', { headers: { Authorization: `Bearer ${token}`, 'X-Campaign-ID': localStorage.getItem('selectedCampaign') || 'medicare' } }),
        axios.get('/api/agent/change-requests', { headers: { Authorization: `Bearer ${token}`, 'X-Campaign-ID': localStorage.getItem('selectedCampaign') || 'medicare' } })
      ]);

      const profile = profileRes.data;
      setAgentProfile(profile);
      setHas2FA(profile.twoFactorEnabled || false);

      // Determine enabled status
      // Group member login: look inside groupMembers for the logged-in member
      const isMemberLogin = profile._isMemberLogin;
      const loggedInMemberEmail = profile._loggedInMemberEmail;

      if (isMemberLogin && loggedInMemberEmail && profile.groupMembers) {
        const member = profile.groupMembers.find(m => m.email === loggedInMemberEmail);
        setAgentEnabled(member ? member.enabled !== false : true);
      } else if (profile.agentType === 'group') {
        // Group admin login — use group-level distributionConfig
        setAgentEnabled(profile.distributionConfig?.enabled !== false);
      } else {
        // Standalone agent
        setAgentEnabled(profile.distributionConfig?.enabled !== false);
      }

      // Check for pending toggle requests
      const emailToCheck = isMemberLogin ? loggedInMemberEmail : profile.email;
      const pending = (requestsRes.data || []).some(
        r => r.status === 'pending' && r.agentEmail === emailToCheck && r.changes?.enabled !== undefined
      );
      setHasPendingToggle(pending);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleToggleSubmit = async () => {
    if (!toggleReason.trim()) {
      toast.error('Please provide a reason for this request.');
      return;
    }
    setSubmittingToggle(true);
    try {
      const isMemberLogin = agentProfile?._isMemberLogin;
      const loggedInMemberEmail = agentProfile?._loggedInMemberEmail;

      await axios.post('/api/agent/change-requests', {
        enabled: !agentEnabled,           // request to flip to opposite state
        toggleReason: toggleReason.trim(),
        ...(isMemberLogin && loggedInMemberEmail ? { agentEmail: loggedInMemberEmail } : {})
      }, { headers: { Authorization: `Bearer ${token}`, 'X-Campaign-ID': localStorage.getItem('selectedCampaign') || 'medicare' } });

      toast.success('Request submitted! An admin will review it shortly.');
      setShowToggleModal(false);
      setToggleReason('');
      setHasPendingToggle(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error submitting request');
    } finally {
      setSubmittingToggle(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/agent/distribution/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="container">
      <div className="dashboard-header">
        <h2>Agent Dashboard</h2>
      </div>

      {/* Admin notifications marquee */}
      <NotificationMarquee />

      {/* ── Enable / Disable Toggle Request Button ── */}
      {agentEnabled !== null && (
        <div style={{ marginTop: '20px', marginBottom: '8px' }}>
          {hasPendingToggle ? (
            <div style={{
              padding: '16px 24px', borderRadius: '12px',
              background: '#fef9c3', border: '2px solid #eab308', color: '#713f12',
              fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <span style={{ fontSize: '22px' }}>⏳</span>
              <span>Your request to <strong>{agentEnabled ? 'pause' : 'enable'}</strong> your campaign is pending admin approval.</span>
            </div>
          ) : (
            <button
              onClick={() => { setToggleReason(''); setShowToggleModal(true); }}
              style={{
                width: '100%', padding: '18px 24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                /* Green = currently active, Red = currently paused — shows TRUE current state */
                background: agentEnabled
                  ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                  : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
                boxShadow: agentEnabled ? '0 4px 16px rgba(22,163,74,0.35)' : '0 4px 16px rgba(220,38,38,0.35)',
                transition: 'all 0.2s', textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {/* Icon reflects TRUE current state */}
                <span style={{ fontSize: '32px', lineHeight: 1 }}>{agentEnabled ? '🟢' : '🔴'}</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                    {agentEnabled ? 'Campaign Active' : 'Campaign Paused'}
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.9, fontWeight: '400' }}>
                    {agentEnabled
                      ? 'You are receiving leads. Click to request pausing your campaign.'
                      : 'You are not receiving leads. Click to request enabling your campaign.'}
                  </div>
                </div>
              </div>
              <span style={{ fontSize: '13px', fontWeight: '700', opacity: 0.9, flexShrink: 0, border: '1px solid rgba(255,255,255,0.5)', borderRadius: '6px', padding: '5px 10px' }}>
                {agentEnabled ? 'Pause Campaign' : 'Enable Campaign'}
              </span>
            </button>
          )}
        </div>
      )}

      {/* 2FA Setup Prompt Banner */}
      {!has2FA && !bannerDismissed && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '20px',
          marginTop: '20px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
          animation: 'slideDown 0.4s ease-out'
        }}>
          <div style={{ fontSize: '40px', flexShrink: 0 }}>🔒</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#92400e', fontSize: '18px', fontWeight: '700' }}>
              Secure Your Account with Two-Factor Authentication
            </h3>
            <p style={{ margin: '0 0 12px 0', color: '#78350f', fontSize: '14px', lineHeight: '1.5' }}>
              Add an extra layer of security to protect your account and opportunity data. 2FA takes just 2 minutes to set up.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link
                to="/agent/setup-2fa"
                className='enable-2fa-btn'
                style={{
                  padding: '10px 20px',
                  background: '#f59e0b',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                }}
              >
                🛡️ Enable 2FA Now
              </Link>
              <button
                onClick={() => setBannerDismissed(true)}
                className='remindme-later-btn'
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: '#f59e0b',
                  border: '2px solid #f59e0b',
                  borderRadius: '6px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Remind Me Later
              </button>
            </div>
          </div>
          <button
            onClick={() => setBannerDismissed(true)}
            style={{
              background: 'transparent', border: 'none', fontSize: '24px',
              color: '#92400e', cursor: 'pointer', padding: '4px', lineHeight: '1', flexShrink: 0
            }}
          >×</button>
        </div>
      )}

      {/* Opportunity Fulfillment Stats */}
      {stats && (
        <div style={{ marginTop: '24px', marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '16px' }}>My Opportunities Fulfillment Stats</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>

            <div style={{ padding: '20px', background: isFlux ? 'white' : '#f0f9ff', borderRadius: isFlux ? '12px' : '8px', border: isFlux ? '3px solid #3FA45F' : '1px solid #bae6fd', boxShadow: isFlux ? '0 4px 16px rgba(20,184,166,0.15)' : '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '13px', color: isFlux ? '#64748b' : '#0369a1', marginBottom: '8px', fontWeight: isFlux ? '600' : 'normal', textTransform: isFlux ? 'uppercase' : 'none' }}>Today's Opportunities</div>
              <div style={{ fontSize: isFlux ? '36px' : '28px', fontWeight: '700', color: isFlux ? '#3FA45F' : '#0c4a6e' }}>{stats.stats?.todaySent || 0}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>opportunities received today</div>
            </div>

            <div style={{ padding: '20px', background: isFlux ? 'white' : '#fef3c7', borderRadius: isFlux ? '12px' : '8px', border: isFlux ? '3px solid #3FA45F' : '1px solid #fde68a', boxShadow: isFlux ? '0 4px 16px rgba(20,184,166,0.15)' : '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '13px', color: isFlux ? '#64748b' : '#b45309', marginBottom: '8px', fontWeight: isFlux ? '600' : 'normal', textTransform: isFlux ? 'uppercase' : 'none' }}>Yesterday's Opportunities</div>
              <div style={{ fontSize: isFlux ? '36px' : '28px', fontWeight: '700', color: isFlux ? '#3FA45F' : '#78350f' }}>{stats.stats?.yesterdaySent || 0}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>opportunities received yesterday</div>
            </div>

            <div style={{ padding: '20px', background: isFlux ? 'white' : '#f0fdf4', borderRadius: isFlux ? '12px' : '8px', border: isFlux ? '3px solid #3FA45F' : '1px solid #bbf7d0', boxShadow: isFlux ? '0 4px 16px rgba(20,184,166,0.15)' : '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '13px', color: isFlux ? '#64748b' : '#15803d', marginBottom: '8px', fontWeight: isFlux ? '600' : 'normal', textTransform: isFlux ? 'uppercase' : 'none' }}>Remaining Opportunities</div>
              <div style={{ fontSize: isFlux ? '36px' : '28px', fontWeight: '700', color: isFlux ? '#3FA45F' : '#14532d' }}>{stats.stats?.totalRemaining || 0}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>opportunities available</div>
            </div>

            <div style={{ padding: '20px', background: isFlux ? 'white' : '#faf5ff', borderRadius: isFlux ? '12px' : '8px', border: isFlux ? '3px solid #3FA45F' : '1px solid #e9d5ff', boxShadow: isFlux ? '0 4px 16px rgba(20,184,166,0.15)' : '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '13px', color: isFlux ? '#64748b' : '#7e22ce', marginBottom: '8px', fontWeight: isFlux ? '600' : 'normal', textTransform: isFlux ? 'uppercase' : 'none' }}>All Time Opportunities</div>
              <div style={{ fontSize: isFlux ? '36px' : '28px', fontWeight: '700', color: isFlux ? '#3FA45F' : '#581c87' }}>{stats.stats?.totalSent || 0}</div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>total opportunities received</div>
            </div>

          </div>
        </div>
      )}

      {/* ── Pipeline Breakdown ── */}
      {pipelineCounts && pipelineCounts.total > 0 && (
        <div style={{ marginTop: '32px', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: 8 }}>
            <h3 style={{ margin: 0 }}>My Pipeline Breakdown</h3>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
              {pipelineCounts.total} total leads in pipeline
            </span>
          </div>

          {/* Progress bar */}
          <div style={{ display: 'flex', height: 12, borderRadius: 8, overflow: 'hidden', marginBottom: 20, background: '#f3f4f6' }}>
            {pipelineCounts.columns.filter(c => c.count > 0).map(c => {
              const s = getColStyle(c.id);
              return (
                <div key={c.id} title={`${c.title}: ${c.count} (${c.pct}%)`}
                  style={{ width: `${c.pct}%`, background: s.text, transition: 'width 0.4s', minWidth: c.count > 0 ? 4 : 0 }} />
              );
            })}
          </div>

          {/* Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14 }}>
            {pipelineCounts.columns.map(c => {
              const s = getColStyle(c.id);
              return (
                <div key={c.id} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 12, padding: '16px 18px' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 12, color: s.text, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    {c.title}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: s.text, lineHeight: 1 }}>{c.count}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    {c.pct}% of total
                  </div>
                  {/* mini bar */}
                  <div style={{ marginTop: 10, height: 4, borderRadius: 4, background: '#e5e7eb', overflow: 'hidden' }}>
                    <div style={{ width: `${c.pct}%`, height: '100%', background: s.text, borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Action Cards */}
      <div className="stats-grid" style={{ marginTop: '32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        <Link to="/agent/profile" className={isFlux ? 'action-card' : 'stat-card'}>
          <div className={isFlux ? 'action-card-icon' : 'stat-icon'} style={{ background: isFlux ? '#3FA45F' : '#4f46e5', color: 'white' }}>
            <FiUser size={24} />
          </div>
          <div className="stat-content">
            <h3>My Profile</h3>
            <p>View your details</p>
          </div>
        </Link>

        <Link to="/agent/request" className={isFlux ? 'action-card' : 'stat-card'}>
          <div className={isFlux ? 'action-card-icon' : 'stat-icon'} style={{ background: '#f59e0b', color: 'white' }}>
            <FiEdit size={24} />
          </div>
          <div className="stat-content">
            <h3>Request Changes</h3>
            <p>Update your information</p>
          </div>
        </Link>
      </div>

      {/* ── Toggle Reason Modal ── */}
      {showToggleModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700' }}>
              {agentEnabled ? '⏸️ Pause Campaign' : '▶️ Enable Campaign'}
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#6b7280', fontSize: '14px', lineHeight: '1.6' }}>
              {agentEnabled
                ? 'Please provide a reason for pausing your campaign. An admin will review and approve this request.'
                : 'Please provide a reason for enabling your campaign. An admin will review and approve this request.'}
            </p>

            <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>
              Reason <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              value={toggleReason}
              onChange={(e) => setToggleReason(e.target.value)}
              placeholder={agentEnabled
                ? 'e.g. Going on vacation July 10–20, please pause leads during this time.'
                : 'e.g. Back from vacation, ready to receive leads again.'}
              rows={4}
              style={{
                width: '100%', padding: '12px', borderRadius: '8px',
                border: '1px solid #d1d5db', fontSize: '14px', resize: 'vertical',
                outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
              }}
              autoFocus
            />

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => { setShowToggleModal(false); setToggleReason(''); }}
                disabled={submittingToggle}
                style={{
                  flex: 1, padding: '12px', borderRadius: '8px',
                  border: '2px solid #e5e7eb', background: 'white',
                  color: '#374151', fontWeight: '600', fontSize: '14px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleSubmit}
                disabled={submittingToggle || !toggleReason.trim()}
                style={{
                  flex: 2, padding: '12px', borderRadius: '8px', border: 'none',
                  /* Red = pausing (destructive action), Green = enabling (positive action) */
                  background: submittingToggle || !toggleReason.trim()
                    ? '#9ca3af'
                    : agentEnabled ? '#dc2626' : '#16a34a',
                  color: 'white', fontWeight: '700', fontSize: '14px',
                  cursor: submittingToggle || !toggleReason.trim() ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                {submittingToggle ? 'Submitting…' : agentEnabled ? 'Submit Pause Request' : 'Submit Enable Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentDashboard;
